/* eslint-disable import/no-cycle */
import get from 'lodash/get';
import at from 'lodash/at';
import isObject from 'lodash/isObject';
import { isEmpty, isPlainObject } from 'lodash';
import { getUTCTimestamp, stripHtml } from '../../utils';

const SHAPES = {
  radio_button: 'oval',
  radio: 'classic',
  cards: 'card',
  product_card: 'product_card',
  default_radio: 'defaultRadio',
};

const getShape = ({ questionId, singleSelect, displayType, question }) => {
  const questionShape = get(question, 'properties.shape');
  if (questionShape) {
    return {
      shape: questionShape,
      overlap: false,
    };
  }
  const { shapeOverride, shape: selectShape, overlap } = singleSelect;
  if (shapeOverride) {
    const field = Object.keys(shapeOverride).find((key) => questionId.search(key) !== -1);
    if (shapeOverride[field]) {
      return shapeOverride[field];
    }
  }
  if (selectShape) {
    return {
      shape: selectShape,
      overlap,
    };
  }
  return {
    shape: SHAPES[displayType],
    overlap: false,
  };
};

const getWidth = (component, pageId, pageOverrides, size = '', screenViewport) => {
  if (screenViewport === 'mobile') {
    if (size === 'S') {
      return '130px';
    }
    return 'L';
  }
  if (size) {
    return size;
  }
  const pageOverridesWidth = get(pageOverrides, [pageId, component, 'width']);
  if (isObject(pageOverridesWidth) && pageOverridesWidth[screenViewport.toLowerCase()]) {
    return pageOverridesWidth[screenViewport.toLowerCase()];
  }

  if (pageOverridesWidth && typeof pageOverridesWidth === 'string') {
    return pageOverrides[pageId][component].width;
  }

  return 'L';
};

const shouldShowPageInfo = (pageId, pageOverrides) => {
  if (pageOverrides[pageId]) {
    return pageOverrides[pageId].showPageInfo;
  }
  return false;
};

/*
 * @description Gets specific theme from config based on key.
 * @requires theme Config based theme object
 * @requires key String
 *
 * @returns Returns key specific theme.
 */
const getThemeFromConfig = (theme, key) => {
  if (!key) return {};
  const pageTheme = get(theme, `config.theme.pageOverrides.${theme.pageId}.${key}`);
  const globalTheme = get(theme, `config.theme.global.${key}`, {});
  return pageTheme || globalTheme;
};

const getLabelProps = (componentType, componentProperties, pageId, pageOverrides) => {
  if (pageOverrides[pageId] && pageOverrides[pageId][componentType]) {
    if (pageId === 'quotereview' && componentType === 'button' && window.innerWidth < 768) {
      return {
        ...pageOverrides[pageId][componentType],
        width: '100%',
      };
    }
    return {
      ...pageOverrides[pageId][componentType],
    };
  }
  return {
    ...componentProperties[componentType],
  };
};

const getTooltipProps = (tooltipTitle, tooltipBody, tooltipHTML) => {
  if (tooltipTitle && tooltipBody) {
    return {
      hasTooltip: true,
      tooltipTitle,
      tooltipBody,
    };
  }
  if (tooltipTitle) {
    return {
      hasTooltip: true,
      tooltipTitle,
      tooltipBody: '',
    };
  }
  if (tooltipBody) {
    return {
      hasTooltip: true,
      tooltipTitle: '',
      tooltipBody,
    };
  }
  if (tooltipHTML) {
    return {
      hasTooltip: true,
      tooltipHTML,
    };
  }
  return {};
};

/**
 * @description Gets CSS Variables defined in config file.
 * @requires theme Config based theme object
 *
 * @returns global - Returns the global CSS variables defined.
 * @returns pageOverride - Returns the page specified CSS variables defined.
 */
const getCSSVariables = (theme) => {
  const screen = get(theme, 'screenViewport', 'desktop');

  const cssVariables = get(theme, `config.theme.cssVars.${screen}`, {});
  const pageId = get(theme, 'pageId', '');

  const global = {};
  const pageOverride = {};
  // Iterating through CSS Variables object.
  Object.keys(cssVariables).forEach((key) => {
    const value = cssVariables[key];

    global[key] = value.global || {};
    if (pageId) {
      pageOverride[key] = value[pageId] || {};
    }
  });
  return {
    global,
    pageOverride,
  };
};

/**
 *
 * @param {Object} theme app theme object
 * @param {String} key css key
 * @param {String} element element key
 * @returns String - css value
 */
const getCssValue = (theme, styleKey, element) => {
  const cssVars = getCSSVariables(theme);
  const cssValue = at(cssVars, [
    `pageOverride.${styleKey}.${element}`,
    `global.${styleKey}.${element}`,
  ]);
  return cssValue?.[0] || cssValue?.[1];
};

// This utility trims forward and backward slashes from the pattern value.
const trimSlashes = (str) => {
  let actualPattern = str;
  if (actualPattern.slice(-1) === '/' && actualPattern.slice(0, 1) === '/') {
    actualPattern = actualPattern.slice(1, actualPattern.length - 1);
  }
  return actualPattern;
};

// Get Question response by id
const getQuestionResponseByID = (answerArray, id) => {
  for (let i = 0; i < answerArray.length; i += 1) {
    if (answerArray[i].question_id === id) {
      return answerArray[i].answer;
    }
  }
  return '';
};

// Get selected response option from select question_id
const getOptionByQuestionId = (questionList, answerList, questionId) => {
  if (!Array.isArray(questionList) || !questionId) return '';
  for (let i = 0; i < questionList.length; i += 1) {
    if (
      questionList[i].question_id === questionId &&
      Array.isArray(questionList[i].response_options)
    ) {
      return questionList[i].response_options.find(
        (option) => getQuestionResponseByID(answerList, questionId) === option.id,
      );
    }
  }
  return '';
};

const getFormatDate = (date) => {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [month, day, year].join('/');
};

const getValidators = (question, answerList = [], questionList = []) => {
  const {
    validations,
    properties,
    is_editable: isEditable,
    is_readonly: isReadOnly,
    display_type: displayType,
    question_text: questionText,
    question_status: questionStatus,
    question_status_message: questionStatusMessage,
  } = question;

  const {
    auto_format: autoFormat,
    pattern,
    metric,
    step,
    mask,
    min_value: minValue,
    max_value: maxValue,
    min_length: minLength,
    max_length: maxLength,
    precision,
    placeholder_text: placeholderText,
    phone_number_validation: phoneNumberValidation,
    blacklist_codes: blacklistCodes,
    must_match: mustMatch,
    lead_trail_zeores: leadTrailZeroes,
    round_up: roundUp,
  } = validations;

  if (maxValue && maxValue.set_date === 'current_date') {
    const { range = {} } = properties;
    const dateIsoString = new Date().toISOString().slice(0, 10);
    maxValue.value = getFormatDate(dateIsoString);
    range.max = getFormatDate(dateIsoString);
  }

  // Calendar Popper Properties
  let popperMinValue;
  let popperMaxValue;
  if (properties && properties.range) {
    const { range = {} } = properties;
    popperMinValue = range.min;
    popperMaxValue = range.max;
  }

  const disabled = !isEditable || isReadOnly;
  const required = get(validations, 'required', {
    value: false,
    error_message: '',
  });

  let customError = {};

  let placeholder = placeholderText?.hide ? '' : questionText.replace(/(<([^>]+)>)/gi, '');
  if (placeholderText && placeholderText.value) {
    placeholder = placeholderText.value;
  }

  let responseFormat;
  const actualAutoFormat = { ...autoFormat };
  if (autoFormat && autoFormat.value) {
    responseFormat = autoFormat.value.toUpperCase();
    autoFormat.value = autoFormat.value.toUpperCase();
  }

  let updatedAutoFormat = autoFormat;
  if (autoFormat && autoFormat.type === 'currency') {
    updatedAutoFormat = {
      format: 'en-US',
      preserveFormattingInCb: true,
      decimalAllowed: autoFormat.decimal_allowed,
    };
    placeholder = actualAutoFormat.value;
  } else if (autoFormat?.value?.indexOf('00') > -1 && placeholderText?.value) {
    responseFormat = placeholderText.value;
  }

  let updatedPatternValue;
  if (pattern && pattern.value) {
    updatedPatternValue = {
      ...pattern,
      value: trimSlashes(pattern.value),
    };
  } else if (pattern && pattern.select_question_id) {
    const option = getOptionByQuestionId(questionList, answerList, pattern.select_question_id);
    if (option && option.pattern) {
      updatedPatternValue = {
        ...pattern,
        value: trimSlashes(decodeURIComponent(option.pattern)),
      };
    }
  }
  customError = {
    error: questionStatus === 'invalid',
    error_message: questionStatusMessage || validations.error_message,
  };

  if (displayType === 'slider') {
    return {
      maxValue: parseInt(maxValue.value, 10),
      step: parseInt(step.value, 10),
      minValue: parseInt(minValue.value, 10),
      currency: updatedAutoFormat,
    };
  }

  let matchQuestionAnswer;

  if (mustMatch && answerList.length > 0) {
    const { question_id: questionId } = mustMatch;
    matchQuestionAnswer = getQuestionResponseByID(answerList, questionId);
  }

  return {
    metric,
    pattern: updatedPatternValue,
    maxLength,
    minLength,
    autoFormat: updatedAutoFormat,
    currency: updatedAutoFormat,
    step: step && step.value ? step.value : '',
    mask,
    minValue,
    maxValue,
    precision,
    required,
    disabled,
    placeholder,
    customError,
    responseFormat,
    popperMinValue,
    popperMaxValue,
    phoneNumberValidation,
    blacklistCodes,
    mustMatch,
    leadTrailZeroes,
    roundUp,
    matchQuestionAnswer,
  };
};

const getCsvString = (options) => {
  const valueArray = [];
  options.forEach(
    (option) => option.response && option.response.value && valueArray.push(option.response.value),
  );
  return valueArray.join('; ');
};

const isPresent = (array, property, value) => {
  let isPresentInArray = false;
  array.forEach((val) => {
    if (val[property] === value) {
      isPresentInArray = true;
    }
  });
  return isPresentInArray;
};

const getDataSelectorOptions = (options) => {
  const dataSelectorOptions = [];
  if (options && Array.isArray(options)) {
    options.forEach((option, index) => {
      const selectorOption = { id: index, label: getCsvString(option) };
      if (!isPresent(dataSelectorOptions, 'label', selectorOption.label)) {
        dataSelectorOptions.push(selectorOption);
      }
    });
  }
  return dataSelectorOptions;
};

// Get response by id
const getResponse = (id, options) => {
  let response = { value: '' };
  options.forEach((option) => {
    if (option.target_question_id === id) {
      response = option.response;
    }
  });
  return response;
};

// Maps response options and selector options
const getMappedOptions = (responseOptions, selectorOptions) => {
  const mappedOptions = [];
  responseOptions.forEach((responseOption) => {
    const options = [];
    selectorOptions.forEach((selectorOption) => {
      let currentOption = { target_question_id: selectorOption.id };
      const response = getResponse(selectorOption.id, responseOption);
      currentOption = { ...currentOption, response };
      options.push(currentOption);
    });
    mappedOptions.push(options);
  });
  return mappedOptions;
};

const getGroupQAriaLabel = (
  screenReaderLabelId,
  questionsArray,
  pageId,
  isEditMode = false,
  listIndex = 0,
) => {
  let ariaInstruction = '';
  let instructionLabelQ;
  let listQ;
  let originalListQs;

  if (!isPlainObject(questionsArray)) {
    listQ = questionsArray.find((q) => q.display_type === 'list');
    if (listQ?.question_id === screenReaderLabelId) return stripHtml(listQ?.question_text);
  }

  const originalList = get(listQ, 'original_questions', []);

  /** when user edits the list question, fetch list questions from questions key */
  if (!isEmpty(listQ?.questions) && screenReaderLabelId && isEditMode) {
    const addedListQs = listQ?.questions;
    const newScreenReaderLabelId = `${screenReaderLabelId}_${listIndex + 1}`;
    const newListQs = get(addedListQs, `${listIndex}[0].questions`, []);
    instructionLabelQ = newListQs.find((q) => q.question_id === newScreenReaderLabelId);
  } else if (isPlainObject(listQ) && screenReaderLabelId) {
    /** when user adds a new list, fetch list questions from original_questions key  */
    const actualListQs = get(originalList, '[0].questions', []);
    originalListQs = !isEmpty(actualListQs) ? actualListQs : originalList;
    instructionLabelQ = originalListQs.find((q) => q.question_id === screenReaderLabelId);
  } else if (screenReaderLabelId === 'QuoteReviewPage' && pageId === 'plan') {
    /** plan premiums radio button question */
    instructionLabelQ = questionsArray;
  } else if (screenReaderLabelId) {
    instructionLabelQ = questionsArray.find((q) => q.question_id === screenReaderLabelId);
  }

  ariaInstruction =
    screenReaderLabelId === 'QuoteReviewPage' && pageId === 'plan'
      ? instructionLabelQ?.question_description
      : instructionLabelQ?.question_text;

  return ariaInstruction ? stripHtml(ariaInstruction) : '';
};

const getTimeStampData = (ref, transactionLog, meta) => {
  const lastSubmittedAt = getUTCTimestamp();
  const prevTransactionLog = transactionLog || [];
  if (prevTransactionLog.length) {
    /* we dont update the transaction_log array if
        a) (the time_start, time_end) of last object of the 'transaction_log' array is same as (ref.current.timeStart
          and lastSubmittedAt)
        b) the difference between the last time_end and lastSubmittedAt is <= 1 millisecond
        c) some times the lastObj of 'time_start' and 'time_end' is same as ref.current.timeStamp
          for reflexive questions.
      */
    let shouldReturnPrevTime = false;
    const lastObj = prevTransactionLog[prevTransactionLog.length - 1];
    if ((new Date(lastSubmittedAt) - new Date(lastObj.time_end)) / 1000 <= 1) {
      shouldReturnPrevTime = true;
    }
    if (!shouldReturnPrevTime && lastObj.time_start === ref.current.timeStart) {
      if (lastObj.time_end === lastSubmittedAt || lastObj.time_end === ref.current.timeStart) {
        shouldReturnPrevTime = true;
      }
    }

    if (shouldReturnPrevTime) {
      return {
        transactionLog: prevTransactionLog,
        lastSubmittedAt,
      };
    }
  }
  const { os, userAgent: browser, platform } = meta;
  return {
    transactionLog: [
      ...prevTransactionLog,
      {
        time_start: ref.current.timeStart,
        time_end: lastSubmittedAt,
        os,
        browser,
        platform,
      },
    ],
    lastSubmittedAt,
  };
};

const MODALTYPES = {
  retry: 'RETRY',
  alertTimeout: 'ALERT_TIMEOUT',
  default: '',
};

export {
  getShape,
  getWidth,
  getThemeFromConfig,
  getLabelProps,
  getFormatDate,
  getValidators,
  shouldShowPageInfo,
  getTooltipProps,
  getDataSelectorOptions,
  getMappedOptions,
  getCSSVariables,
  getCssValue,
  getTimeStampData,
  getGroupQAriaLabel,
  MODALTYPES,
};
